import { useState, useMemo } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import { Calendar, ChevronLeft, ChevronRight, X, Check } from 'lucide-react';
import { cn } from '@utils/Cn.js';

export interface MonthPickerProps {
  value?: string | string[] | null; // Format: YYYY-MM or array of YYYY-MM
  onChange: (value: string | string[] | null) => void;
  placeholder?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  multiSelect?: boolean;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const FULL_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function parseValues(val?: string | string[] | null): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return val.split(',').map((s) => s.trim()).filter(Boolean);
}

function formatMonthYearLabel(values: string[]): string | null {
  if (values.length === 0) return null;
  if (values.length === 1) {
    const single = values[0];
    if (!single) return null;
    const [yearStr, monthStr] = single.split('-');
    if (!yearStr || !monthStr) return null;
    const monthIdx = parseInt(monthStr, 10) - 1;
    if (isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) return null;
    return `${MONTH_NAMES[monthIdx]} ${yearStr}`;
  }
  return `${String(values.length)} Months`;
}

export default function MonthPicker({
  value,
  onChange,
  placeholder = 'Select Month',
  className,
  align = 'start',
  multiSelect = true,
}: MonthPickerProps) {
  const [open, setOpen] = useState(false);

  const selectedValues = useMemo(() => parseValues(value), [value]);
  const [draftValues, setDraftValues] = useState<string[]>(selectedValues);

  const activeValues = multiSelect && open ? draftValues : selectedValues;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIdx = now.getMonth();
  const currentMonthValue = `${currentYear}-${String(currentMonthIdx + 1).padStart(2, '0')}`;

  const firstSelectedYear = useMemo(() => {
    const first = activeValues[0];
    if (!first) return currentYear;
    const year = parseInt(first.split('-')[0] ?? '', 10);
    return isNaN(year) ? currentYear : year;
  }, [activeValues, currentYear]);

  const [viewYear, setViewYear] = useState<number>(firstSelectedYear);

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setDraftValues(selectedValues);
      setViewYear(firstSelectedYear);
    } else if (multiSelect) {
      onChange(draftValues);
    }
    setOpen(newOpen);
  };

  const handleSelectMonth = (e: React.MouseEvent, monthIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const formatted = `${viewYear}-${String(monthIdx + 1).padStart(2, '0')}`;
    if (multiSelect) {
      const exists = draftValues.includes(formatted);
      const next = exists
        ? draftValues.filter((v) => v !== formatted)
        : [...draftValues, formatted];
      setDraftValues(next);
    } else {
      onChange(formatted);
      setOpen(false);
    }
  };

  const handleApplyDone = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (multiSelect) {
      onChange(draftValues);
    }
    setOpen(false);
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (multiSelect) {
      setDraftValues([]);
      onChange([]);
    } else {
      onChange(null);
    }
    setOpen(false);
  };

  const formattedLabel = formatMonthYearLabel(selectedValues);

  // Quick preset: Next Month
  const nextMonthValue = useMemo(() => {
    const nextDate = new Date(currentYear, currentMonthIdx + 1, 1);
    return `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
  }, [currentYear, currentMonthIdx]);

  return (
    <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-11 items-center justify-between gap-2 rounded-2xl border px-3.5 text-xs font-bold transition-all cursor-pointer select-none',
            selectedValues.length > 0
              ? 'border-slate/40 bg-slate/10 text-slate hover:bg-slate/20 dark:bg-slate/20 dark:text-slate-light dark:hover:bg-slate/30 shadow-xs'
              : 'border-line bg-paper/90 text-ink-soft hover:border-line-strong hover:bg-surface hover:text-ink',
            className,
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Calendar
              size={15}
              className={cn('shrink-0 transition-colors', selectedValues.length > 0 ? 'text-slate' : 'text-ink-faint')}
            />
            <span className="truncate">{formattedLabel ?? placeholder}</span>
          </div>

          {selectedValues.length > 0 ? (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleClear();
              }}
              title="Clear month filter"
              className="shrink-0 flex h-5 w-5 items-center justify-center rounded-full text-slate/70 hover:bg-slate/20 hover:text-slate transition"
            >
              <X size={13} className="stroke-[2.5]" />
            </span>
          ) : (
            <ChevronRight size={14} className="shrink-0 text-ink-faint rotate-90" />
          )}
        </button>
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'z-[9999] w-[300px] rounded-2xl border border-line bg-surface p-4 shadow-[0_20px_50px_rgba(8,19,31,0.24)] outline-none backdrop-blur-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'duration-150',
          )}
        >
          {/* Header Year Navigator */}
          <div className="flex items-center justify-between pb-3 border-b border-line">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setViewYear((y) => y - 1);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-line bg-paper text-ink-soft hover:bg-surface hover:text-ink active:scale-95 transition cursor-pointer"
              title="Previous Year"
            >
              <ChevronLeft size={16} />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setViewYear(currentYear);
              }}
              title="Click to reset to current year"
              className="text-sm font-extrabold text-ink hover:text-slate transition flex items-center gap-1.5 cursor-pointer"
            >
              <span>{viewYear}</span>
              {viewYear !== currentYear && (
                <span className="text-[10px] font-bold text-ink-faint uppercase tracking-wider">
                  (Reset)
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setViewYear((y) => y + 1);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-line bg-paper text-ink-soft hover:bg-surface hover:text-ink active:scale-95 transition cursor-pointer"
              title="Next Year"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Month 3x4 Grid */}
          <div className="grid grid-cols-3 gap-2 pt-3 pb-3">
            {MONTH_NAMES.map((name, idx) => {
              const formatted = `${viewYear}-${String(idx + 1).padStart(2, '0')}`;
              const isSelected = activeValues.includes(formatted);
              const isThisMonth = currentYear === viewYear && currentMonthIdx === idx;

              return (
                <button
                  key={name}
                  type="button"
                  onClick={(e) => { handleSelectMonth(e, idx); }}
                  title={`${FULL_MONTH_NAMES[idx]} ${viewYear}`}
                  className={cn(
                    'relative flex h-10 items-center justify-center rounded-xl border text-xs font-bold transition-all duration-150 cursor-pointer select-none',
                    isSelected
                      ? 'border-slate bg-slate text-white shadow-md shadow-slate/25'
                      : 'border-line/60 bg-paper/50 text-ink-soft hover:border-line-strong hover:bg-paper hover:text-ink',
                    isThisMonth && !isSelected && 'border-slate/40 text-slate font-extrabold bg-slate/5',
                  )}
                >
                  <span>{name}</span>

                  {/* Indicator dot for current month */}
                  {isThisMonth && (
                    <span
                      className={cn(
                        'absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full',
                        isSelected ? 'bg-white' : 'bg-slate',
                      )}
                    />
                  )}

                  {isSelected && (
                    <span className="absolute bottom-1 right-1">
                      <Check size={10} strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Quick Presets & Actions Footer */}
          <div className="flex items-center justify-between gap-1.5 pt-3 border-t border-line text-[11px] font-bold">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (multiSelect) {
                    const exists = draftValues.includes(currentMonthValue);
                    setDraftValues(exists ? draftValues.filter((v) => v !== currentMonthValue) : [...draftValues, currentMonthValue]);
                  } else {
                    onChange(currentMonthValue);
                    setOpen(false);
                  }
                }}
                className={cn(
                  'px-2 py-1 rounded-lg border transition cursor-pointer',
                  activeValues.includes(currentMonthValue)
                    ? 'border-slate bg-slate/15 text-slate font-extrabold'
                    : 'border-line bg-paper text-ink-soft hover:text-ink hover:bg-surface',
                )}
              >
                This Month
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (multiSelect) {
                    const exists = draftValues.includes(nextMonthValue);
                    setDraftValues(exists ? draftValues.filter((v) => v !== nextMonthValue) : [...draftValues, nextMonthValue]);
                  } else {
                    onChange(nextMonthValue);
                    setOpen(false);
                  }
                }}
                className={cn(
                  'px-2 py-1 rounded-lg border transition cursor-pointer',
                  activeValues.includes(nextMonthValue)
                    ? 'border-slate bg-slate/15 text-slate font-extrabold'
                    : 'border-line bg-paper text-ink-soft hover:text-ink hover:bg-surface',
                )}
              >
                Next Month
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              {activeValues.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-red-fg hover:underline font-extrabold px-1 cursor-pointer"
                >
                  Clear
                </button>
              )}
              {multiSelect && (
                <button
                  type="button"
                  onClick={handleApplyDone}
                  className="px-3 py-1 rounded-lg bg-slate text-white font-extrabold hover:bg-slate-soft transition cursor-pointer shadow-xs"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
